import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {CenterBox} from "@pg-design/grid";
import {fadeInAnimation, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {IRPStore} from "../../app/rp_reducer";
import {IMarketplaceArchitectOfferSummary} from "../../architect_application/api/fetch_marketplace_architect_promoted_offers_by_region";
import {ArchitectMultileadModalContent} from "../../architect_application/components/ArchitectMultileadModalContent";
import {ArchitectOfferSummaryModalContent} from "../../architect_application/components/ArchitectOfferSummaryModalContent";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {LeadModalAskForFullOfferForm} from "../../lead/components/lead_modal/lead_modal_ask_for_full_offer_form/LeadModalAskForFullOfferForm";
import {LeadModalNegotiationForm} from "../../lead/components/lead_modal/lead_modal_negotiation_form/LeadModalNegotiationForm";
import {LeadModalPriceConversationStep} from "../../lead/components/lead_modal/lead_modal_price_conversation_step/LeadModalPriceConversationStep";
import {LeadModalTopicForm} from "../../lead/components/lead_modal/lead_modal_topic_form/LeadModalTopicForm";
import {useLeadRequiredParams} from "../../lead/hooks/use_lead_required_params";
import {hitMultileadRecommendationShown} from "../../lead/tracking/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../lead/types/MultileadVersion";
import {RecommendationSource} from "../../lead/types/RecommendationSource";
import {getSentLeadFormValuesFromStorage} from "../../lead/utils/sent_lead_form_values";
import {IRegionAncestor} from "../../offer/list/actions/offer_list_selected_data_actions";
import {hitGTMEcommerceRemoveFromCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_remove_from_cart";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmStepApplicationClose} from "../../tracking/google_tag_manager/gtm_event_step_application_close";
import {multiLeadApplicationsActionsTypes} from "../actions/multi_lead_applications_actions";
import {fetchVendorRecommendations} from "../api/fetch_vendor_recommendations";
import {initialEmptyApplicationValues} from "../constants/application_form";
import {vendorRecommendationsDistanceInKm} from "../constants/multi_lead_constants";
import {useApplicationModalArray} from "../hooks/use_application_modal_array";
import {useHomeMatchPreferencesStorage} from "../hooks/use_home_match_preferences_storage";
import {useMultiLeadRecommendations} from "../hooks/use_multi_lead_recommendations";
import {ModalStepActionTypes} from "../reducers/modal_step_reducer";
import {IApplication} from "../types/IApplication";
import {IMultiLeadStepPayload} from "../types/IMultiLeadStepPayload";
import {IMultileadSummaryStepState} from "../types/IMultileadSummaryStepState";
import {INotificationConsentStepPayload} from "../types/INotificationConsentStepPayload";
import {IOriginalApplicationProperty} from "../types/IOriginalApplicationProperty";
import {ISetMultiLeadSummaryStepPayload} from "../types/ISetMultiLeadSummaryStepPayload";
import {ISetPreferencesStepPayload} from "../types/ISetPreferencesStepPayload";
import {LeadModalStep} from "../types/LeadModalStep";
import {MultiLeadRecommendationType} from "../types/MultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getPreferencesFromApplicationData} from "../utils/get_preferences_from_lead_data";
import {getStorageSentApplicationsIdListByOffer} from "../utils/get_storage_sent_applications_id_list_by_offer";
import {fetchSimpleMultiLeadRecommendations} from "../utils/recommendations/fetch_simple_multilead_recommendations";
import {report} from "../utils/recommended_application/analytics_events";
import {MultiLead} from "./multi_lead/MultiLead";
import {AppliedPMOffer} from "./multi_lead_promo/AppliedPMOffer";
import {PMPromoModalLayout} from "./multi_lead_promo/PMPromoModalLayout";
import {PreferencesModal} from "./preferences_steps/components/PreferencesModal";
import {Application} from "./Application";
import {ApplicationGoodbyeContentModal} from "./ApplicationGoodbyeContentModal";
import {ApplicationStyledModal} from "./ApplicationStyledResponsiveModal";
import {AppliedArchitectInteriorDesign} from "./AppliedArchitectInteriorDesign";
import {FormSuccessInfoContainer} from "./FormSuccessInfoContainer";
import {NotificationConsentConfirmation} from "./NotificationConsentConfirmation";

export const ApplicationModalArray = () => {
    const dispatch = useAppDispatch();
    const {vendorId, offerId, propertyId} = useLeadRequiredParams();

    const {
        modalStepState,
        dispatchModalStepState,
        isOpened,
        source,
        sourceSection,
        parametrizedStoreHash,
        customFormText,
        initialLeadFormValues,
        leadData,
        price
    } = useApplicationModalArray();

    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    // TODO: 3 props below are only user in LeadGeneratorSuccessInfo scenario, create a new component for this scenario
    const customSourceSection = useAppSelector((state) => state.mainLead.customSourceSection);
    const preventClosingWhenNoData = useAppSelector((state) => state.mainLead.preventClosingWhenNoData);
    const roomCount = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.rooms) as number;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const currentViewType = useAppSelector((store: IRPStore) => store.viewType.current);
    const recommendationType = propertyId ? MultiLeadRecommendationType.PROPERTY : MultiLeadRecommendationType.OFFER;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = useAppSelector((state) => state.application[parametrizedStoreHash]?.application) || null;

    const {areRecommendationsEmpty, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId,
        offerId
    });

    const onModalClose = () => {
        if (modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD && mainLeadType) {
            gtmStepApplicationClose(mainLeadType);
        }

        const shouldTrackRemoveFromCart =
            (modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ||
                modalStepState.step === LeadModalStep.APPLICATION_FORM_FULL_OFFER ||
                modalStepState.step === LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION) &&
            leadData &&
            mainLeadType;

        if (shouldTrackRemoveFromCart) {
            hitGTMEcommerceRemoveFromCart({
                data: leadData,
                viewType: currentViewType,
                leadType: mainLeadType,
                sourceSection: customSourceSection || sourceSection
            });
        }

        const hasOriginalApplicationResponse = Boolean(modalStepState.originalApplicationResponse);
        const isClosedOnMultileadStep = modalStepState.step === LeadModalStep.MULTILEAD;

        if (hasOriginalApplicationResponse && isClosedOnMultileadStep && !isAbroad) {
            dispatchModalStepState({
                type: ModalStepActionTypes.SET_PREFERENCES_STEP,
                payload: {
                    originalApplicationResponse: modalStepState.originalApplicationResponse,
                    multiLeadSummaryItems: [],
                    offerId,
                    propertyId
                }
            });
            return;
        }

        dispatchModalStepState({type: ModalStepActionTypes.CLOSE});
    };

    const onArchitectOfferApplied = (architectOfferDetails: IMarketplaceArchitectOfferSummary, architectRegion: IRegionAncestor | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload: {architectOfferDetails, architectRegion}
        });
    };

    const dispatchNextStepWithoutPayload = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP
        });
    };

    const setNotificationConsentStep = (payload: INotificationConsentStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP,
            payload
        });
    };

    const setAfterMultiLeadStep = (payload: IMultileadSummaryStepState) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload
        });
    };

    const setMultiLeadStep = (payload: IMultiLeadStepPayload) => {
        if (!isMultileadStepEnabled) {
            return;
        }

        dispatchModalStepState({
            type: ModalStepActionTypes.SET_MULTILEAD_STEP,
            payload
        });
    };

    const setModalClose = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_CLOSE
        });
    };

    const setPreferencesStep = (payload: ISetPreferencesStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_PREFERENCES_STEP,
            payload
        });
    };

    const setPreferencesStepWithCountryCheck = (payload: ISetPreferencesStepPayload) => {
        // Only show preferences for Polish offers
        if (!isAbroad) {
            setPreferencesStep(payload);
            return;
        }

        // For abroad offers, check if we need to show notification consent
        const showNotificationConsent = !payload.originalApplicationResponse?.marketing_email || !payload.originalApplicationResponse?.marketing_sms;

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const handlePreferencesStepFinish = (payload: ISetPreferencesStepPayload) => {
        const showNotificationConsent =
            payload.originalApplicationResponse && (!payload.originalApplicationResponse.marketing_email || !payload.originalApplicationResponse.marketing_sms);

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const updateMainLeadResponse = (sentApplication: IApplication | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA,
            payload: {
                originalApplicationResponse: sentApplication
            }
        });
    };

    const handleCloseMultiLead = (payload?: ISetMultiLeadSummaryStepPayload) => {
        const startedAtMultilead = initialStep === LeadModalStep.MULTILEAD;
        if (isMultileadStepEnabled || startedAtMultilead) {
            report.modalClose(recommendationType, currentViewType);

            const hasAnyLeadBesideOriginal = Boolean(payload && payload.multiLeadSummaryItems && payload.multiLeadSummaryItems.length > 1);
            const hasOnlyOriginalSuccesfullLead = startedAtMultilead ? Boolean(originalApplication) : Boolean(modalStepState.originalApplicationResponse);
            const shouldSetSummaryStep = hasAnyLeadBesideOriginal || hasOnlyOriginalSuccesfullLead;

            if (shouldSetSummaryStep) {
                setPreferencesStep({
                    originalApplicationResponse: modalStepState.originalApplicationResponse,
                    multiLeadSummaryItems: payload?.multiLeadSummaryItems || [],
                    offerId,
                    propertyId
                });
                return;
            }
        }

        dispatch({type: multiLeadApplicationsActionsTypes.reset, id: parametrizedStoreHash});
        setModalClose();
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const onSingleLeadSuccess = async (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        updateMainLeadResponse(sentApplication);

        if (leadData && leadData.offer?.detail?.region.country === Country.POLAND) {
            putPreferences(getPreferencesFromApplicationData(leadData));
        }

        let recommendedList: IRecommendedProperty[] | IRecommendedOffer[] | null = null;
        let multiLeadProperty: IOriginalApplicationProperty | null = property;
        const isAbroadLead = leadData?.offer?.detail?.region.country !== Country.POLAND;

        // Fetch recommendations only if multilead is enabled
        if (isMultileadStepEnabled && !isAbroadLead) {
            recommendedList = await fetchSimpleMultiLeadRecommendations({
                dispatch,
                offer,
                property,
                sentApplication,
                recommendedFilters
            });
        }

        if (!isAbroadLead) {
            const vendorRecommendations = await fetchVendorRecommendations(
                {
                    offerId: offer?.id,
                    originalApplicationProperty: property,
                    excludedOfferIds: getStorageSentApplicationsIdListByOffer(offer, {limit: 50, distance: vendorRecommendationsDistanceInKm * 1000}),
                    radius: vendorRecommendationsDistanceInKm
                },
                dispatch
            );

            recommendedList = vendorRecommendations.results;
            multiLeadProperty = vendorRecommendations.leadProperty;
        }

        if (isMultileadStepEnabled && (!recommendedList || recommendedList.length === 0)) {
            const recommendationsType = property ? MultiLeadRecommendationType.PROPERTY : MultiLeadRecommendationType.OFFER;

            hitMultileadRecommendationShown({
                recommendationType: isAbroadLead ? recommendationsType : MultiLeadRecommendationType.VENDOR,
                recommendationId: null,
                viewType: currentViewType,
                recommendationSource: recommendationSource,
                multileadVersion:
                    recommendationsType === MultiLeadRecommendationType.PROPERTY ? MultileadVersion.RP_LIST_PROPERTY : MultileadVersion.RP_LIST_OFFER
            });
        }

        const originalApplication = sentApplication ? sentApplication : null;
        const isWithOriginalApplicationResponse = Boolean(originalApplication);
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;
        const isWithRecommendations = recommendedList && recommendedList.length > 0;

        if (isMultileadStepEnabled && isWithOriginalApplicationResponse && isWithRecommendations) {
            setMultiLeadStep({
                originalApplicationResponse: originalApplication,
                recommendedOffers: recommendedList,
                shouldCloseOnOverlayClick: false,
                multiLeadProperty,
                recommendationsWithPriceFilter
            });
            return;
        }

        if (!isAbroad && isWithOriginalApplicationResponse && modalStepState.step !== LeadModalStep.PREFERENCES) {
            setPreferencesStep({
                originalApplicationResponse: originalApplication,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });
            return;
        }

        const isMktOptinSelected = sentApplication?.marketing_email || sentApplication?.marketing_sms;
        if (isWithOriginalApplicationResponse && !isMktOptinSelected) {
            setNotificationConsentStep({
                originalApplicationResponse: originalApplication
            });
            return;
        }

        if (isWithOriginalApplicationResponse && !isAbroadLead) {
            // WARNING: setPreferencesStep should ONLY BE USED in context of polish offers.
            setPreferencesStep({
                originalApplicationResponse: originalApplication,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });

            return;
        }
        setModalClose();
        return;
    };

    // Render
    const splitModal = modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD || modalStepState.step === LeadModalStep.GOODBYE_INFO;

    const renderContent = () => {
        // render cases
        switch (modalStepState.step) {
            case LeadModalStep.APPLICATION_FORM_STANDARD: {
                return initialLeadFormValues && leadData ? (
                    <Application
                        vendorId={vendorId}
                        offerId={offerId || undefined}
                        propertyId={propertyId || undefined}
                        initialLeadFormValues={initialLeadFormValues}
                        leadData={leadData}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        source={source}
                        sourceSection={sourceSection}
                        alwaysShowFields
                        hideRodo={window.innerWidth > 1024}
                        closeModal={onModalClose}
                        customFormText={customFormText || undefined}
                    />
                ) : null;
            }
            case LeadModalStep.APPLICATION_FORM_WITH_CONVERSATION: {
                return (
                    <LeadModalPriceConversationStep
                        handleModalClose={onModalClose}
                        handleSetPreferencesStep={setPreferencesStepWithCountryCheck}
                        handleUpdateMainLeadResponse={updateMainLeadResponse}
                        price={price}
                        storeHash={parametrizedStoreHash}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_FULL_OFFER: {
                return (
                    <LeadModalAskForFullOfferForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return (
                    <LeadModalNegotiationForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                        price={price}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return (
                    <LeadModalTopicForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case LeadModalStep.NOTIFICATION_CONSENT: {
                return (
                    <NotificationConsentConfirmation
                        storeHash={parametrizedStoreHash}
                        result={modalStepState.originalApplicationResponse}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        onModalClose={onModalClose}
                        sourceSection={sourceSection}
                    />
                );
            }
            case LeadModalStep.SUCCESS_INFO: {
                return (
                    <FormSuccessInfoContainer
                        isModal
                        modalStepState={modalStepState}
                        onClick={() =>
                            dispatchModalStepState({
                                type: ModalStepActionTypes.NEXT_STEP
                            })
                        }
                        shouldRenderRecommendations
                        onModalClose={onModalClose}
                    />
                );
            }
            case LeadModalStep.GOODBYE_INFO: {
                return (
                    <ApplicationGoodbyeContentModal storeHash={parametrizedStoreHash} source={source} sourceSection={sourceSection} closeModal={onModalClose} />
                );
            }
            case LeadModalStep.MULTILEAD: {
                if ((!isMultileadStepEnabled && initialStep === LeadModalStep.MULTILEAD) || areRecommendationsLoading) {
                    return (
                        <CenterBox>
                            <Loader />
                        </CenterBox>
                    );
                }
                if (!isMultileadStepEnabled) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return null;
                }

                const sentLeadFormValues = getSentLeadFormValuesFromStorage();
                if (!sentLeadFormValues && !preventClosingWhenNoData) {
                    // application data should be filled at this point. But just in case:
                    onModalClose();
                    return null;
                }

                const multiLeadOriginalApplication = modalStepState.originalApplicationResponse || originalApplication || null;

                if (areRecommendationsEmpty) {
                    setPreferencesStepWithCountryCheck({
                        originalApplicationResponse: multiLeadOriginalApplication,
                        multiLeadSummaryItems: [],
                        propertyId,
                        offerId
                    });
                }

                const originalApplicationFormValues = sentLeadFormValues
                    ? {...sentLeadFormValues, phone: getPhoneDataFromString(sentLeadFormValues.phone)}
                    : {...initialEmptyApplicationValues, rooms: [roomCount]};

                return (
                    <MultiLead
                        offerId={offerId || undefined}
                        propertyId={propertyId || undefined}
                        originalApplicationResponse={multiLeadOriginalApplication}
                        originalApplicationFormValues={originalApplicationFormValues}
                        storeHash={parametrizedStoreHash}
                        sourceSection={ApplicationSourceSection.MULTILEAD}
                        recommendationType={recommendationType}
                        recommendationsWithPriceFilter={modalStepState.recommendationsWithPriceFilter}
                        customSourceSection={customSourceSection || undefined}
                        handleModalClose={handleCloseMultiLead}
                        handleSetMultiLeadSummaryStep={setAfterMultiLeadStep}
                    />
                );
            }
            case LeadModalStep.ARCHITECT_OFFER: {
                return (
                    <ArchitectOfferSummaryModalContent
                        storeHash={parametrizedStoreHash}
                        architectOfferDetails={modalStepState.architectOfferDetails}
                        architectRegion={modalStepState.architectRegion}
                        onArchitectOfferApplied={onArchitectOfferApplied}
                        userData={{
                            name: modalStepState.originalApplicationResponse?.name,
                            email: modalStepState.originalApplicationResponse?.email,
                            phone: modalStepState.originalApplicationResponse?.phone
                        }}
                    />
                );
            }
            case LeadModalStep.PM_OFFER: {
                return (
                    <PMPromoModalLayout
                        originalApplicationResponse={modalStepState.originalApplicationResponse}
                        storeHash={parametrizedStoreHash}
                        leadData={leadData}
                        onSuccessfulSubmit={dispatchNextStepWithoutPayload}
                    />
                );
            }
            case LeadModalStep.PM_APPLIED_OFFER: {
                return <AppliedPMOffer onCloseButtonClick={dispatchNextStepWithoutPayload} />;
            }
            case LeadModalStep.APPLIED_ARCHITECT_OFFER: {
                return (
                    <AppliedArchitectInteriorDesign
                        architectOfferDetails={modalStepState.architectOfferDetails}
                        onCloseButtonClick={dispatchNextStepWithoutPayload}
                    />
                );
            }
            case LeadModalStep.ARCHITECT_MULTILEAD: {
                return (
                    <ArchitectMultileadModalContent
                        region={modalStepState.architectRegion?.name}
                        userData={{
                            name: modalStepState.originalApplicationResponse?.name || "",
                            email: modalStepState.originalApplicationResponse?.email || "",
                            phone: modalStepState.originalApplicationResponse?.phone || "",
                            area: ""
                        }}
                        mainAppliedArchitectOffer={modalStepState.architectOfferDetails}
                    />
                );
            }
            case LeadModalStep.PREFERENCES: {
                return (
                    <PreferencesModal
                        applicationPayload={{...modalStepState, offerId, propertyId}}
                        onClose={handlePreferencesStepFinish}
                        preferencesSurveySource={PreferencesSurveySource.AFTER_LEAD}
                    />
                );
            }

            case LeadModalStep.WAITING:
            default:
                return <Loader size="lg" fullWidth />;
        }
    };

    const shouldCloseOnOverlayClick = modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ? false : modalStepState.shouldCloseOnOverlayClick;

    return (
        <FormModal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            css={getModalStyle(modalStepState.step)}
            showCloseButton={
                ![
                    LeadModalStep.MULTILEAD,
                    LeadModalStep.APPLICATION_FORM_STANDARD,
                    LeadModalStep.GOODBYE_INFO,
                    LeadModalStep.PREFERENCES,
                    LeadModalStep.APPLICATION_FORM_FULL_OFFER,
                    LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION,
                    LeadModalStep.APPLICATION_FORM_WITH_TOPIC
                ].includes(modalStepState.step)
            }
        >
            {modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ? (
                <div css={newApplicationWrapStyle}>
                    <div css={newApplicationHolderStyle}>{renderContent()}</div>
                </div>
            ) : (
                <ApplicationWrapper>
                    <ApplicationHolder wide={!splitModal}>{renderContent()}</ApplicationHolder>
                </ApplicationWrapper>
            )}
        </FormModal>
    );
};

// Styles
const getModalStyle = (step: LeadModalStep) => (theme: Theme) => {
    switch (step) {
        case LeadModalStep.NOTIFICATION_CONSENT:
        case LeadModalStep.SUCCESS_INFO:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 896px;
                    height: auto;
                }
            `;
        case LeadModalStep.GOODBYE_INFO:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 896px;
                    height: 644px;
                }
            `;
        case LeadModalStep.MULTILEAD:
        case LeadModalStep.ARCHITECT_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 950px;
                    height: auto;
                }
            `;
        case LeadModalStep.APPLIED_ARCHITECT_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 860px;
                    height: auto;
                }
            `;
        case LeadModalStep.PM_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 950px;
                    height: 520px;
                }
            `;
        case LeadModalStep.PM_APPLIED_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 850px;
                    height: auto;
                }
            `;
        case LeadModalStep.ARCHITECT_MULTILEAD:
            return css`
                width: 100%;

                @media (min-width: ${theme.breakpoints.md}) {
                    max-width: 745px;
                    height: auto;
                }
            `;
        case LeadModalStep.PREFERENCES: {
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 850px;
                }
            `;
        }
    }
};

const FormModal = styled(ApplicationStyledModal)`
    ${fadeInAnimation("0.1s")};
    ${p(0)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        width: unset;
        height: unset;
    }
`;

const newApplicationWrapStyle = css`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    height: 100%;
`;

const newApplicationHolderStyle = css`
    width: 100%;
    min-width: 28rem;
    overflow-y: auto;
    position: relative;
`;

const ApplicationWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    height: 100%;
`;

interface IApplicationHolderProps {
    wide?: boolean;
}

const ApplicationHolder = styled.div<IApplicationHolderProps>`
    width: 100%;
    height: 100%;
    min-width: 28rem;
    overflow-y: auto;
    position: relative;
`;
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {CenterBox} from "@pg-design/grid";
import {fadeInAnimation, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {getPhoneDataFromString} from "@pg-mono/string-utils";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {IRPStore} from "../../app/rp_reducer";
import {IMarketplaceArchitectOfferSummary} from "../../architect_application/api/fetch_marketplace_architect_promoted_offers_by_region";
import {ArchitectMultileadModalContent} from "../../architect_application/components/ArchitectMultileadModalContent";
import {ArchitectOfferSummaryModalContent} from "../../architect_application/components/ArchitectOfferSummaryModalContent";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {LeadModalAskForFullOfferForm} from "../../lead/components/lead_modal/lead_modal_ask_for_full_offer_form/LeadModalAskForFullOfferForm";
import {LeadModalNegotiationForm} from "../../lead/components/lead_modal/lead_modal_negotiation_form/LeadModalNegotiationForm";
import {LeadModalPriceConversationStep} from "../../lead/components/lead_modal/lead_modal_price_conversation_step/LeadModalPriceConversationStep";
import {LeadModalTopicForm} from "../../lead/components/lead_modal/lead_modal_topic_form/LeadModalTopicForm";
import {useLeadRequiredParams} from "../../lead/hooks/use_lead_required_params";
import {hitMultileadRecommendationShown} from "../../lead/tracking/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../lead/types/MultileadVersion";
import {RecommendationSource} from "../../lead/types/RecommendationSource";
import {getSentLeadFormValuesFromStorage} from "../../lead/utils/sent_lead_form_values";
import {IRegionAncestor} from "../../offer/list/actions/offer_list_selected_data_actions";
import {hitGTMEcommerceRemoveFromCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_remove_from_cart";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IRecommendedOffer} from "../../offer/types/IRecommendedOffer";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IRecommendedProperty} from "../../property/types/IRecommendedProperty";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmStepApplicationClose} from "../../tracking/google_tag_manager/gtm_event_step_application_close";
import {multiLeadApplicationsActionsTypes} from "../actions/multi_lead_applications_actions";
import {fetchVendorRecommendations} from "../api/fetch_vendor_recommendations";
import {initialEmptyApplicationValues} from "../constants/application_form";
import {vendorRecommendationsDistanceInKm} from "../constants/multi_lead_constants";
import {useApplicationModalArray} from "../hooks/use_application_modal_array";
import {useHomeMatchPreferencesStorage} from "../hooks/use_home_match_preferences_storage";
import {useMultiLeadRecommendations} from "../hooks/use_multi_lead_recommendations";
import {ModalStepActionTypes} from "../reducers/modal_step_reducer";
import {IApplication} from "../types/IApplication";
import {IMultiLeadStepPayload} from "../types/IMultiLeadStepPayload";
import {IMultileadSummaryStepState} from "../types/IMultileadSummaryStepState";
import {INotificationConsentStepPayload} from "../types/INotificationConsentStepPayload";
import {IOriginalApplicationProperty} from "../types/IOriginalApplicationProperty";
import {ISetMultiLeadSummaryStepPayload} from "../types/ISetMultiLeadSummaryStepPayload";
import {ISetPreferencesStepPayload} from "../types/ISetPreferencesStepPayload";
import {LeadModalStep} from "../types/LeadModalStep";
import {MultiLeadRecommendationType} from "../types/MultiLeadRecommendationType";
import {ApplicationSource, ApplicationSourceSection} from "../utils/ApplicationSource";
import {getPreferencesFromApplicationData} from "../utils/get_preferences_from_lead_data";
import {getStorageSentApplicationsIdListByOffer} from "../utils/get_storage_sent_applications_id_list_by_offer";
import {fetchSimpleMultiLeadRecommendations} from "../utils/recommendations/fetch_simple_multilead_recommendations";
import {report} from "../utils/recommended_application/analytics_events";
import {MultiLead} from "./multi_lead/MultiLead";
import {AppliedPMOffer} from "./multi_lead_promo/AppliedPMOffer";
import {PMPromoModalLayout} from "./multi_lead_promo/PMPromoModalLayout";
import {PreferencesModal} from "./preferences_steps/components/PreferencesModal";
import {Application} from "./Application";
import {ApplicationGoodbyeContentModal} from "./ApplicationGoodbyeContentModal";
import {ApplicationStyledModal} from "./ApplicationStyledResponsiveModal";
import {AppliedArchitectInteriorDesign} from "./AppliedArchitectInteriorDesign";
import {FormSuccessInfoContainer} from "./FormSuccessInfoContainer";
import {NotificationConsentConfirmation} from "./NotificationConsentConfirmation";

export const ApplicationModalArray = () => {
    const dispatch = useAppDispatch();
    const {vendorId, offerId, propertyId} = useLeadRequiredParams();

    const {
        modalStepState,
        dispatchModalStepState,
        isOpened,
        source,
        sourceSection,
        parametrizedStoreHash,
        customFormText,
        initialLeadFormValues,
        leadData,
        price
    } = useApplicationModalArray();

    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    // TODO: 3 props below are only user in LeadGeneratorSuccessInfo scenario, create a new component for this scenario
    const customSourceSection = useAppSelector((state) => state.mainLead.customSourceSection);
    const preventClosingWhenNoData = useAppSelector((state) => state.mainLead.preventClosingWhenNoData);
    const roomCount = useAppSelector((store) => store.application[parametrizedStoreHash]?.property?.detail?.rooms) as number;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const currentViewType = useAppSelector((store: IRPStore) => store.viewType.current);
    const recommendationType = propertyId ? MultiLeadRecommendationType.PROPERTY : MultiLeadRecommendationType.OFFER;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = useAppSelector((state) => state.application[parametrizedStoreHash]?.application) || null;

    const {areRecommendationsEmpty, areRecommendationsLoading} = useMultiLeadRecommendations({
        propertyId,
        offerId
    });

    const onModalClose = () => {
        if (modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD && mainLeadType) {
            gtmStepApplicationClose(mainLeadType);
        }

        const shouldTrackRemoveFromCart =
            (modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ||
                modalStepState.step === LeadModalStep.APPLICATION_FORM_FULL_OFFER ||
                modalStepState.step === LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION) &&
            leadData &&
            mainLeadType;

        if (shouldTrackRemoveFromCart) {
            hitGTMEcommerceRemoveFromCart({
                data: leadData,
                viewType: currentViewType,
                leadType: mainLeadType,
                sourceSection: customSourceSection || sourceSection
            });
        }

        const hasOriginalApplicationResponse = Boolean(modalStepState.originalApplicationResponse);
        const isClosedOnMultileadStep = modalStepState.step === LeadModalStep.MULTILEAD;

        if (hasOriginalApplicationResponse && isClosedOnMultileadStep && !isAbroad) {
            dispatchModalStepState({
                type: ModalStepActionTypes.SET_PREFERENCES_STEP,
                payload: {
                    originalApplicationResponse: modalStepState.originalApplicationResponse,
                    multiLeadSummaryItems: [],
                    offerId,
                    propertyId
                }
            });
            return;
        }

        dispatchModalStepState({type: ModalStepActionTypes.CLOSE});
    };

    const onArchitectOfferApplied = (architectOfferDetails: IMarketplaceArchitectOfferSummary, architectRegion: IRegionAncestor | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload: {architectOfferDetails, architectRegion}
        });
    };

    const dispatchNextStepWithoutPayload = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP
        });
    };

    const setNotificationConsentStep = (payload: INotificationConsentStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_NOTIFICATION_CONSENT_STEP,
            payload
        });
    };

    const setAfterMultiLeadStep = (payload: IMultileadSummaryStepState) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.NEXT_STEP,
            payload
        });
    };

    const setMultiLeadStep = (payload: IMultiLeadStepPayload) => {
        if (!isMultileadStepEnabled) {
            return;
        }

        dispatchModalStepState({
            type: ModalStepActionTypes.SET_MULTILEAD_STEP,
            payload
        });
    };

    const setModalClose = () => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_CLOSE
        });
    };

    const setPreferencesStep = (payload: ISetPreferencesStepPayload) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.SET_PREFERENCES_STEP,
            payload
        });
    };

    const setPreferencesStepWithCountryCheck = (payload: ISetPreferencesStepPayload) => {
        // Only show preferences for Polish offers
        if (!isAbroad) {
            setPreferencesStep(payload);
            return;
        }

        // For abroad offers, check if we need to show notification consent
        const showNotificationConsent = !payload.originalApplicationResponse?.marketing_email || !payload.originalApplicationResponse?.marketing_sms;

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const handlePreferencesStepFinish = (payload: ISetPreferencesStepPayload) => {
        const showNotificationConsent =
            payload.originalApplicationResponse && (!payload.originalApplicationResponse.marketing_email || !payload.originalApplicationResponse.marketing_sms);

        if (showNotificationConsent) {
            setNotificationConsentStep(payload);
            return;
        }
        setModalClose();
    };

    const updateMainLeadResponse = (sentApplication: IApplication | null) => {
        dispatchModalStepState({
            type: ModalStepActionTypes.UPDATE_SINGLE_LEAD_RESPONSE_DATA,
            payload: {
                originalApplicationResponse: sentApplication
            }
        });
    };

    const handleCloseMultiLead = (payload?: ISetMultiLeadSummaryStepPayload) => {
        const startedAtMultilead = initialStep === LeadModalStep.MULTILEAD;
        if (isMultileadStepEnabled || startedAtMultilead) {
            report.modalClose(recommendationType, currentViewType);

            const hasAnyLeadBesideOriginal = Boolean(payload && payload.multiLeadSummaryItems && payload.multiLeadSummaryItems.length > 1);
            const hasOnlyOriginalSuccesfullLead = startedAtMultilead ? Boolean(originalApplication) : Boolean(modalStepState.originalApplicationResponse);
            const shouldSetSummaryStep = hasAnyLeadBesideOriginal || hasOnlyOriginalSuccesfullLead;

            if (shouldSetSummaryStep) {
                setPreferencesStep({
                    originalApplicationResponse: modalStepState.originalApplicationResponse,
                    multiLeadSummaryItems: payload?.multiLeadSummaryItems || [],
                    offerId,
                    propertyId
                });
                return;
            }
        }

        dispatch({type: multiLeadApplicationsActionsTypes.reset, id: parametrizedStoreHash});
        setModalClose();
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const onSingleLeadSuccess = async (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        updateMainLeadResponse(sentApplication);

        if (leadData && leadData.offer?.detail?.region.country === Country.POLAND) {
            putPreferences(getPreferencesFromApplicationData(leadData));
        }

        let recommendedList: IRecommendedProperty[] | IRecommendedOffer[] | null = null;
        let multiLeadProperty: IOriginalApplicationProperty | null = property;
        const isAbroadLead = leadData?.offer?.detail?.region.country !== Country.POLAND;

        // Fetch recommendations only if multilead is enabled
        if (isMultileadStepEnabled && !isAbroadLead) {
            recommendedList = await fetchSimpleMultiLeadRecommendations({
                dispatch,
                offer,
                property,
                sentApplication,
                recommendedFilters
            });
        }

        if (!isAbroadLead) {
            const vendorRecommendations = await fetchVendorRecommendations(
                {
                    offerId: offer?.id,
                    originalApplicationProperty: property,
                    excludedOfferIds: getStorageSentApplicationsIdListByOffer(offer, {limit: 50, distance: vendorRecommendationsDistanceInKm * 1000}),
                    radius: vendorRecommendationsDistanceInKm
                },
                dispatch
            );

            recommendedList = vendorRecommendations.results;
            multiLeadProperty = vendorRecommendations.leadProperty;
        }

        if (isMultileadStepEnabled && (!recommendedList || recommendedList.length === 0)) {
            const recommendationsType = property ? MultiLeadRecommendationType.PROPERTY : MultiLeadRecommendationType.OFFER;

            hitMultileadRecommendationShown({
                recommendationType: isAbroadLead ? recommendationsType : MultiLeadRecommendationType.VENDOR,
                recommendationId: null,
                viewType: currentViewType,
                recommendationSource: recommendationSource,
                multileadVersion:
                    recommendationsType === MultiLeadRecommendationType.PROPERTY ? MultileadVersion.RP_LIST_PROPERTY : MultileadVersion.RP_LIST_OFFER
            });
        }

        const originalApplication = sentApplication ? sentApplication : null;
        const isWithOriginalApplicationResponse = Boolean(originalApplication);
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;
        const isWithRecommendations = recommendedList && recommendedList.length > 0;

        if (isMultileadStepEnabled && isWithOriginalApplicationResponse && isWithRecommendations) {
            setMultiLeadStep({
                originalApplicationResponse: originalApplication,
                recommendedOffers: recommendedList,
                shouldCloseOnOverlayClick: false,
                multiLeadProperty,
                recommendationsWithPriceFilter
            });
            return;
        }

        if (!isAbroad && isWithOriginalApplicationResponse && modalStepState.step !== LeadModalStep.PREFERENCES) {
            setPreferencesStep({
                originalApplicationResponse: originalApplication,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });
            return;
        }

        const isMktOptinSelected = sentApplication?.marketing_email || sentApplication?.marketing_sms;
        if (isWithOriginalApplicationResponse && !isMktOptinSelected) {
            setNotificationConsentStep({
                originalApplicationResponse: originalApplication
            });
            return;
        }

        if (isWithOriginalApplicationResponse && !isAbroadLead) {
            // WARNING: setPreferencesStep should ONLY BE USED in context of polish offers.
            setPreferencesStep({
                originalApplicationResponse: originalApplication,
                multiLeadSummaryItems: [],
                offerId,
                propertyId
            });

            return;
        }
        setModalClose();
        return;
    };

    // Render
    const splitModal = modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD || modalStepState.step === LeadModalStep.GOODBYE_INFO;

    const renderContent = () => {
        // render cases
        switch (modalStepState.step) {
            case LeadModalStep.APPLICATION_FORM_STANDARD: {
                return initialLeadFormValues && leadData ? (
                    <Application
                        vendorId={vendorId}
                        offerId={offerId || undefined}
                        propertyId={propertyId || undefined}
                        initialLeadFormValues={initialLeadFormValues}
                        leadData={leadData}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        source={source}
                        sourceSection={sourceSection}
                        alwaysShowFields
                        hideRodo={window.innerWidth > 1024}
                        closeModal={onModalClose}
                        customFormText={customFormText || undefined}
                    />
                ) : null;
            }
            case LeadModalStep.APPLICATION_FORM_WITH_CONVERSATION: {
                return (
                    <LeadModalPriceConversationStep
                        handleModalClose={onModalClose}
                        handleSetPreferencesStep={setPreferencesStepWithCountryCheck}
                        handleUpdateMainLeadResponse={updateMainLeadResponse}
                        price={price}
                        storeHash={parametrizedStoreHash}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_FULL_OFFER: {
                return (
                    <LeadModalAskForFullOfferForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return (
                    <LeadModalNegotiationForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                        price={price}
                    />
                );
            }
            case LeadModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return (
                    <LeadModalTopicForm
                        handleModalClose={onModalClose}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        storeHash={parametrizedStoreHash}
                        initialLeadFormValues={initialLeadFormValues}
                        offerId={offerId}
                        propertyId={propertyId}
                    />
                );
            }
            case LeadModalStep.NOTIFICATION_CONSENT: {
                return (
                    <NotificationConsentConfirmation
                        storeHash={parametrizedStoreHash}
                        result={modalStepState.originalApplicationResponse}
                        onSuccessfulSubmit={onSingleLeadSuccess}
                        onModalClose={onModalClose}
                        sourceSection={sourceSection}
                    />
                );
            }
            case LeadModalStep.SUCCESS_INFO: {
                return (
                    <FormSuccessInfoContainer
                        isModal
                        modalStepState={modalStepState}
                        onClick={() =>
                            dispatchModalStepState({
                                type: ModalStepActionTypes.NEXT_STEP
                            })
                        }
                        shouldRenderRecommendations
                        onModalClose={onModalClose}
                    />
                );
            }
            case LeadModalStep.GOODBYE_INFO: {
                return (
                    <ApplicationGoodbyeContentModal storeHash={parametrizedStoreHash} source={source} sourceSection={sourceSection} closeModal={onModalClose} />
                );
            }
            case LeadModalStep.MULTILEAD: {
                if ((!isMultileadStepEnabled && initialStep === LeadModalStep.MULTILEAD) || areRecommendationsLoading) {
                    return (
                        <CenterBox>
                            <Loader />
                        </CenterBox>
                    );
                }
                if (!isMultileadStepEnabled) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return null;
                }

                const sentLeadFormValues = getSentLeadFormValuesFromStorage();
                if (!sentLeadFormValues && !preventClosingWhenNoData) {
                    // application data should be filled at this point. But just in case:
                    onModalClose();
                    return null;
                }

                const multiLeadOriginalApplication = modalStepState.originalApplicationResponse || originalApplication || null;

                if (areRecommendationsEmpty) {
                    setPreferencesStepWithCountryCheck({
                        originalApplicationResponse: multiLeadOriginalApplication,
                        multiLeadSummaryItems: [],
                        propertyId,
                        offerId
                    });
                }

                const originalApplicationFormValues = sentLeadFormValues
                    ? {...sentLeadFormValues, phone: getPhoneDataFromString(sentLeadFormValues.phone)}
                    : {...initialEmptyApplicationValues, rooms: [roomCount]};

                return (
                    <MultiLead
                        offerId={offerId || undefined}
                        propertyId={propertyId || undefined}
                        originalApplicationResponse={multiLeadOriginalApplication}
                        originalApplicationFormValues={originalApplicationFormValues}
                        storeHash={parametrizedStoreHash}
                        sourceSection={ApplicationSourceSection.MULTILEAD}
                        recommendationType={recommendationType}
                        recommendationsWithPriceFilter={modalStepState.recommendationsWithPriceFilter}
                        customSourceSection={customSourceSection || undefined}
                        handleModalClose={handleCloseMultiLead}
                        handleSetMultiLeadSummaryStep={setAfterMultiLeadStep}
                    />
                );
            }
            case LeadModalStep.ARCHITECT_OFFER: {
                return (
                    <ArchitectOfferSummaryModalContent
                        storeHash={parametrizedStoreHash}
                        architectOfferDetails={modalStepState.architectOfferDetails}
                        architectRegion={modalStepState.architectRegion}
                        onArchitectOfferApplied={onArchitectOfferApplied}
                        userData={{
                            name: modalStepState.originalApplicationResponse?.name,
                            email: modalStepState.originalApplicationResponse?.email,
                            phone: modalStepState.originalApplicationResponse?.phone
                        }}
                    />
                );
            }
            case LeadModalStep.PM_OFFER: {
                return (
                    <PMPromoModalLayout
                        originalApplicationResponse={modalStepState.originalApplicationResponse}
                        storeHash={parametrizedStoreHash}
                        leadData={leadData}
                        onSuccessfulSubmit={dispatchNextStepWithoutPayload}
                    />
                );
            }
            case LeadModalStep.PM_APPLIED_OFFER: {
                return <AppliedPMOffer onCloseButtonClick={dispatchNextStepWithoutPayload} />;
            }
            case LeadModalStep.APPLIED_ARCHITECT_OFFER: {
                return (
                    <AppliedArchitectInteriorDesign
                        architectOfferDetails={modalStepState.architectOfferDetails}
                        onCloseButtonClick={dispatchNextStepWithoutPayload}
                    />
                );
            }
            case LeadModalStep.ARCHITECT_MULTILEAD: {
                return (
                    <ArchitectMultileadModalContent
                        region={modalStepState.architectRegion?.name}
                        userData={{
                            name: modalStepState.originalApplicationResponse?.name || "",
                            email: modalStepState.originalApplicationResponse?.email || "",
                            phone: modalStepState.originalApplicationResponse?.phone || "",
                            area: ""
                        }}
                        mainAppliedArchitectOffer={modalStepState.architectOfferDetails}
                    />
                );
            }
            case LeadModalStep.PREFERENCES: {
                return (
                    <PreferencesModal
                        applicationPayload={{...modalStepState, offerId, propertyId}}
                        onClose={handlePreferencesStepFinish}
                        preferencesSurveySource={PreferencesSurveySource.AFTER_LEAD}
                    />
                );
            }

            case LeadModalStep.WAITING:
            default:
                return <Loader size="lg" fullWidth />;
        }
    };

    const shouldCloseOnOverlayClick = modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ? false : modalStepState.shouldCloseOnOverlayClick;

    return (
        <FormModal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            css={getModalStyle(modalStepState.step)}
            showCloseButton={
                ![
                    LeadModalStep.MULTILEAD,
                    LeadModalStep.APPLICATION_FORM_STANDARD,
                    LeadModalStep.GOODBYE_INFO,
                    LeadModalStep.PREFERENCES,
                    LeadModalStep.APPLICATION_FORM_FULL_OFFER,
                    LeadModalStep.APPLICATION_FORM_WITH_NEGOTIATION,
                    LeadModalStep.APPLICATION_FORM_WITH_TOPIC
                ].includes(modalStepState.step)
            }
        >
            {modalStepState.step === LeadModalStep.APPLICATION_FORM_STANDARD ? (
                <div css={newApplicationWrapStyle}>
                    <div css={newApplicationHolderStyle}>{renderContent()}</div>
                </div>
            ) : (
                <ApplicationWrapper>
                    <ApplicationHolder wide={!splitModal}>{renderContent()}</ApplicationHolder>
                </ApplicationWrapper>
            )}
        </FormModal>
    );
};

// Styles
const getModalStyle = (step: LeadModalStep) => (theme: Theme) => {
    switch (step) {
        case LeadModalStep.NOTIFICATION_CONSENT:
        case LeadModalStep.SUCCESS_INFO:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 896px;
                    height: auto;
                }
            `;
        case LeadModalStep.GOODBYE_INFO:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 896px;
                    height: 644px;
                }
            `;
        case LeadModalStep.MULTILEAD:
        case LeadModalStep.ARCHITECT_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 950px;
                    height: auto;
                }
            `;
        case LeadModalStep.APPLIED_ARCHITECT_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 860px;
                    height: auto;
                }
            `;
        case LeadModalStep.PM_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 950px;
                    height: 520px;
                }
            `;
        case LeadModalStep.PM_APPLIED_OFFER:
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 850px;
                    height: auto;
                }
            `;
        case LeadModalStep.ARCHITECT_MULTILEAD:
            return css`
                width: 100%;

                @media (min-width: ${theme.breakpoints.md}) {
                    max-width: 745px;
                    height: auto;
                }
            `;
        case LeadModalStep.PREFERENCES: {
            return css`
                @media (min-width: ${theme.breakpoints.md}) {
                    width: 850px;
                }
            `;
        }
    }
};

const FormModal = styled(ApplicationStyledModal)`
    ${fadeInAnimation("0.1s")};
    ${p(0)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        width: unset;
        height: unset;
    }
`;

const newApplicationWrapStyle = css`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    height: 100%;
`;

const newApplicationHolderStyle = css`
    width: 100%;
    min-width: 28rem;
    overflow-y: auto;
    position: relative;
`;

const ApplicationWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    height: 100%;
`;

interface IApplicationHolderProps {
    wide?: boolean;
}

const ApplicationHolder = styled.div<IApplicationHolderProps>`
    width: 100%;
    height: 100%;
    min-width: 28rem;
    overflow-y: auto;
    position: relative;
`;
